import React, { useMemo, useRef, useState } from 'react';

/**
 * A small multi-series line chart with a hover crosshair and tooltip.
 *
 * Built here rather than pulled in as a dependency for the same reason
 * UsersPage's sparkline was: this app has no charting library and adding one
 * for three line charts would be more code to keep current than the SVG is.
 *
 * The rules it follows, per the dataviz skill:
 *  - **One y-axis, always.** Every series handed to this component must share
 *    a unit. Two measures of different scale are two charts, never two
 *    y-scales — `unit` is a single prop precisely so that a caller trying to
 *    mix milliseconds and percentages has nowhere to put the second one.
 *  - 2px lines, recessive grid/axes, no marker on every point (only on hover
 *    and on the last point, which is direct-labelled).
 *  - Identity is never colour-alone: a legend is always rendered for two or
 *    more series, the last point of each is direct-labelled, and the caller
 *    also renders the numbers as a table.
 *  - Values and labels wear ink tokens; the colour lives on the mark.
 *
 * `series`: [{ key, label, color, values: [{ x, y }] }] — y may be null for a
 * day with no data, which breaks the line rather than drawing through the gap
 * (a missing nightly rollup and a genuine zero are different facts).
 */
const PAD = { top: 12, right: 12, bottom: 22, left: 44 };

export default function LineTrend({ series, height = 180, unit = '', format = (v) => v, yMax: forcedMax }) {
  const [hover, setHover] = useState(null);
  const svgRef = useRef(null);

  const xs = useMemo(
    () => Array.from(new Set(series.flatMap((s) => s.values.map((v) => v.x)))).sort(),
    [series],
  );

  const yMax = useMemo(() => {
    if (forcedMax != null) return forcedMax;
    const all = series.flatMap((s) => s.values.map((v) => v.y)).filter((v) => v != null);
    return Math.max(1, ...all);
  }, [series, forcedMax]);

  if (xs.length === 0) {
    return <p className="rounded-xl border border-hairline bg-surface p-4 text-sm text-ink-muted">No data yet.</p>;
  }

  const W = 720;
  const H = height;
  const plotW = W - PAD.left - PAD.right;
  const plotH = H - PAD.top - PAD.bottom;
  const xAt = (x) => PAD.left + (xs.length === 1 ? plotW / 2 : (xs.indexOf(x) / (xs.length - 1)) * plotW);
  const yAt = (y) => PAD.top + plotH - (y / yMax) * plotH;

  // A null y ends the current path and starts a new one, so a gap in the
  // data reads as a gap rather than as a straight line through it.
  const pathFor = (values) => {
    const byX = new Map(values.map((v) => [v.x, v.y]));
    let d = '';
    let open = false;
    xs.forEach((x) => {
      const y = byX.get(x);
      if (y == null) { open = false; return; }
      d += `${open ? 'L' : 'M'}${xAt(x).toFixed(1)} ${yAt(y).toFixed(1)} `;
      open = true;
    });
    return d.trim();
  };

  const onMove = (e) => {
    const rect = svgRef.current.getBoundingClientRect();
    const px = ((e.clientX - rect.left) / rect.width) * W;
    const ratio = Math.min(1, Math.max(0, (px - PAD.left) / plotW));
    setHover(xs[Math.round(ratio * (xs.length - 1))]);
  };

  const gridLines = [0, 0.25, 0.5, 0.75, 1];

  return (
    <div className="relative rounded-xl border border-hairline bg-surface p-4">
      <svg
        ref={svgRef}
        viewBox={`0 0 ${W} ${H}`}
        className="w-full"
        role="img"
        onMouseMove={onMove}
        onMouseLeave={() => setHover(null)}
      >
        {gridLines.map((g) => (
          <g key={g}>
            <line
              x1={PAD.left} x2={W - PAD.right}
              y1={PAD.top + plotH * (1 - g)} y2={PAD.top + plotH * (1 - g)}
              stroke={g === 0 ? '#c3c2b7' : '#e1e0d9'} strokeWidth="1"
            />
            <text
              x={PAD.left - 6} y={PAD.top + plotH * (1 - g) + 3}
              textAnchor="end" fontSize="9" fill="#898781"
            >
              {format(yMax * g)}
            </text>
          </g>
        ))}

        {hover && (
          // Solid, not dashed: a dashed vertical rule reads as a threshold
          // or a projection, and this is neither — it is just where the
          // pointer is.
          <line
            x1={xAt(hover)} x2={xAt(hover)} y1={PAD.top} y2={PAD.top + plotH}
            stroke="#c3c2b7" strokeWidth="1"
          />
        )}

        {series.map((s) => (
          <path key={s.key} d={pathFor(s.values)} fill="none" stroke={s.color}
                strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        ))}

        {/* Only the last point gets a permanent marker — a dot on every point
            turns a trend line into a scatter of noise. */}
        {series.map((s) => {
          const last = [...s.values].reverse().find((v) => v.y != null);
          if (!last) return null;
          return (
            <circle key={s.key} cx={xAt(last.x)} cy={yAt(last.y)} r="4"
                    fill={s.color} stroke="#fcfcfb" strokeWidth="2" />
          );
        })}

        {hover && series.map((s) => {
          const point = s.values.find((v) => v.x === hover);
          if (!point || point.y == null) return null;
          return (
            <circle key={s.key} cx={xAt(hover)} cy={yAt(point.y)} r="4"
                    fill={s.color} stroke="#fcfcfb" strokeWidth="2" />
          );
        })}

        <text x={PAD.left} y={H - 6} fontSize="9" fill="#898781">{xs[0]}</text>
        {xs.length > 1 && (
          <text x={W - PAD.right} y={H - 6} fontSize="9" fill="#898781" textAnchor="end">
            {xs[xs.length - 1]}
          </text>
        )}
      </svg>

      {hover && (
        <div className="pointer-events-none absolute right-4 top-4 rounded-lg border border-hairline bg-surface px-3 py-2 text-xs shadow-lg">
          <div className="mb-1 font-medium text-ink">{hover}</div>
          {series.map((s) => {
            const point = s.values.find((v) => v.x === hover);
            return (
              <div key={s.key} className="flex items-center gap-2 text-ink-secondary">
                <span className="h-2 w-2 rounded-full" style={{ background: s.color }} aria-hidden="true" />
                <span>{s.label}</span>
                <span className="ml-auto font-medium text-ink">
                  {point && point.y != null ? `${format(point.y)}${unit}` : '-'}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* Always present for two or more series; a single series is named by
          the section heading and needs no legend box. */}
      {series.length > 1 && (
        <div className="mt-2 flex flex-wrap items-center gap-4 text-xs text-ink-secondary">
          {series.map((s) => (
            <span key={s.key} className="inline-flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full" style={{ background: s.color }} aria-hidden="true" />
              {s.label}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
